import { ComponentRef, Injectable, ViewContainerRef } from '@angular/core';
import { COMPONENT_MANIFESTS } from '@ucx-js/chat-ui';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { getSemafoneConfig } from '../../utils/getSemafoneConfig';
import { selectSafeSendUrl } from '../state/live-chat/live-chat.reducer';
import { selectEmailAddress } from '../state/user-info/user-info.reducer';
import { appConfig } from '../../init/getAppConfig';
import { loadCreditCardCommonText } from '../../utils/loadCreditCardCommonText';
import { getComponentAction } from '../state/get-component-action';
import { ActionType, ComponentType, OverlayType } from '../types/enum';
import { selectCurationList } from '../state/post-escalation-curation/post-escalation-curation.reducer';

@Injectable({
  providedIn: 'root',
})
export class ComponentFactoryService {
  constructor(
    private translateService: TranslateService,
    private store: Store
  ) {}

  async loadPostEscalationMenuComponent(
    overlayComponentRef: ViewContainerRef | undefined
  ): Promise<void> {
    const postEscalationMenuComponent = await this.eagerOrLazyLoadComponent(
      ComponentType.PostEscalationMenuComponent
    );
    const postEscalation = await firstValueFrom(
      this.store.select(selectCurationList)
    );
    const language = this.translateService.currentLang;
    const categories = postEscalation?.find(
      (item) => item.language == language
    )?.list;

    if (
      overlayComponentRef &&
      postEscalationMenuComponent &&
      categories?.length
    ) {
      overlayComponentRef.clear();
      const componentData = {
        categoryHeader: this.translateService.instant(
          'POSTESCALATION.CATEGORY_HEADER'
        ),
        productHeader: this.translateService.instant(
          'POSTESCALATION.PRODUCT_HEADER'
        ),
        categorySubtitle: this.translateService.instant(
          'POSTESCALATION.CATEGORY_SUBTITLE'
        ),
        productSubtitle: this.translateService.instant(
          'POSTESCALATION.PRODUCT_SUBTITLE'
        ),
        specificProductPreSubtitle: this.translateService.instant(
          'POSTESCALATION.SPECIFIC_PRODUCT_PRE_SUBTITLE'
        ),
        specificProductPostSubtitle: this.translateService.instant(
          'POSTESCALATION.SPECIFIC_PRODUCT_POST_SUBTITLE'
        ),
        categories: categories,
        footerHeader: this.translateService.instant('END_CHAT_FOOTER_HEADER'),
        footerBodyText: this.translateService.instant('END_CHAT_FOOTER_TEXT'),
        footerOkBtnText: this.translateService.instant('END_CHAT_BUTTON_TEXT'),
        footerCancelBtnText: this.translateService.instant(
          'CONTINUE_CHAT_BUTTON_TEXT'
        ),
      };
      const componentActions = {
        onEmitPostEscalationMenuSelection:
          ActionType.onPostEscalationMenuSelection,
        onCancelPostEscalationMenuSelection:
          ActionType.onCancelPostEscalationMenuSelection,
      };
      this.createDynamicComponent(
        overlayComponentRef,
        postEscalationMenuComponent,
        componentData,
        componentActions
      );
    }
  }

  async loadConfirmationOverlayComponent(
    overlayComponentRef: ViewContainerRef
  ): Promise<void> {
    const overlay = await this.eagerOrLazyLoadComponent(
      ComponentType.OverlayComponent
    );
    if (overlayComponentRef && overlay) {
      overlayComponentRef.clear();
      const componentData = {
        isFull: false,
        isOverlayOpen: true,
        isCloseVisible: true,
        isFooterVisible: true,
        header: this.translateService.instant('END_CHAT_FOOTER_HEADER'),
        bodyText: this.translateService.instant('END_CHAT_FOOTER_TEXT'),
        okBtnText: this.translateService.instant('END_CHAT_BUTTON_TEXT'),
        cancelBtnText: this.translateService.instant(
          'CONTINUE_CHAT_BUTTON_TEXT'
        ),
      };
      const componentActions = {
        onOkClicked: ActionType.closeConfirmationOverlay,
        onCancelClicked: ActionType.cancelConfirmationOverlay,
        onCloseOverlay: ActionType.cancelConfirmationOverlay,
      };
      this.createDynamicComponent(
        overlayComponentRef,
        overlay,
        componentData,
        componentActions
      );
    }
  }

  async loadPaymentOverlayComponent(
    overlayComponentRef: ViewContainerRef | undefined
  ): Promise<void> {
    const paymentOverlayComponent = await this.eagerOrLazyLoadComponent(
      ComponentType.PaymentOverlayComponent
    );
    if (overlayComponentRef && paymentOverlayComponent) {
      overlayComponentRef.clear();
      const componentData = {
        paymentData: {
          paymentAmountInputLabel:
            this.translateService.instant('PAYMENT.AMOUNT'),
          paymentAmountErrorMessage: this.translateService.instant(
            'PAYMENT.AMOUNT_ERROR'
          ),
          paymentAmountLocal: this.translateService.instant('PAYMENT.LOCALE'),
          paymentAmountSeparator:
            this.translateService.instant('PAYMENT.SEPARATOR'),
          creditCardInfo: {
            creditCardFormData: loadCreditCardCommonText(this.translateService),
            semafoneConfig: getSemafoneConfig(),
          },
        },
        paymentSubmitText: this.translateService.instant('PAYMENT.SUBMIT'),
        paymentCancelBtnText: this.translateService.instant('PAYMENT.CANCEL'),
      };
      const componentActions = {
        onClickSubmit: ActionType.HandlePayment,
        onClickCancel: ActionType.HandleCancelPayment,
      };
      this.createDynamicComponent(
        overlayComponentRef,
        paymentOverlayComponent,
        componentData,
        componentActions
      );
    }
  }

  async loadEmailTranscriptComponent(
    overlayComponentRef: ViewContainerRef
  ): Promise<void> {
    const overlay = await this.eagerOrLazyLoadComponent(
      ComponentType.EmailTranscriptComponent
    );
    if (overlayComponentRef && overlay) {
      overlayComponentRef.clear();
      const userEmailAddress = await firstValueFrom(
        this.store.select(selectEmailAddress)
      );
      const componentData = {
        header: this.translateService.instant('EMAIL.TITLE'),
        bodyText: this.translateService.instant('EMAIL.DESCRIPTION'),
        submitText: this.translateService.instant('EMAIL.SEND_BUTTON'),
        cancelText: this.translateService.instant('EMAIL.CLOSE_CHAT'),
        emailField: {
          label: this.translateService.instant('EMAIL.EMAIL_LABEL'),
          placeholder: this.translateService.instant('EMAIL.EMAIL_PLACEHOLDER'),
          value: userEmailAddress,
        },
        requiredErrorMessage: this.translateService.instant(
          'EMAIL.ERRORS.EMAIL.REQUIRED'
        ),
        emailErrorMessage: this.translateService.instant(
          'EMAIL.ERRORS.EMAIL.FORMAT'
        ),
      };
      const componentActions = {
        onSubmitEmail: ActionType.requestEmailTranscript,
        onCloseClicked: ActionType.closeEmailTranscript,
      };
      this.createDynamicComponent(
        overlayComponentRef,
        overlay,
        componentData,
        componentActions
      );
    }
  }

  async loadIframeOverlayComponent(
    overlayComponentRef: ViewContainerRef
  ): Promise<void> {
    const overlay = await this.eagerOrLazyLoadComponent(
      ComponentType.IframeComponent
    );
    const safeSendUrl = await firstValueFrom(
      this.store.select(selectSafeSendUrl)
    );
    if (overlayComponentRef && overlay) {
      overlayComponentRef.clear();
      const componentData = {
        iframeSrc: safeSendUrl,
      };
      const componentActions = {};
      this.createDynamicComponent(
        overlayComponentRef,
        overlay,
        componentData,
        componentActions
      );
    }
  }

  loadOverlayComponent(
    overlayComponentRef: ViewContainerRef,
    overlayType: OverlayType
  ): void {
    switch (overlayType) {
      case OverlayType.Confirmation:
        this.loadConfirmationOverlayComponent(overlayComponentRef);
        break;
      case OverlayType.PostEscalation:
        this.loadPostEscalationMenuComponent(overlayComponentRef);
        break;
      case OverlayType.Transcript:
        this.loadEmailTranscriptComponent(overlayComponentRef);
        break;
      case OverlayType.CreditCard:
        this.loadPaymentOverlayComponent(overlayComponentRef);
        break;
      case OverlayType.Iframe:
        this.loadIframeOverlayComponent(overlayComponentRef);
        break;
    }
  }

  async loadMessageComponents(
    messageContainerRef: ViewContainerRef | undefined,
    messages: Readonly<Message[]>,
    index?: number
  ) {
    if (messageContainerRef) {
      for (const message of messages) {
        if (message.messageComponent?.component) {
          const component = await this.eagerOrLazyLoadComponent(
            message.messageComponent?.component
          );
          if (component) {
            this.createDynamicComponent(
              messageContainerRef,
              component,
              message.messageComponent.componentData,
              message.messageComponent.actions,
              index
            );
          }
        }
      }
    }
  }

  async loadAlertBanner(
    alertConfig,
    alertViewContainer: ViewContainerRef
  ): Promise<void> {
    const alertComponent = await this.eagerOrLazyLoadComponent(
      ComponentType.AlertComponent
    );
    if (alertViewContainer && alertComponent) {
      alertViewContainer.clear();
      const componentData = {
        data: alertConfig,
        config: {
          autoHideTiming: alertConfig?.autoHideTiming
            ? alertConfig?.autoHideTiming
            : appConfig.alertAutoHideTiming,
        },
      };
      this.createDynamicComponent(
        alertViewContainer,
        alertComponent,
        componentData
      );
    }
  }

  async eagerOrLazyLoadComponent(componentId): Promise<any> {
    const manifest = COMPONENT_MANIFESTS.find(
      (item) => item.componentId === componentId
    );
    if (manifest?.loader) {
      return manifest.loader();
    } else if (manifest.component) {
      return manifest.component;
    }
  }

  createDynamicComponent(
    containerRef: ViewContainerRef,
    component,
    data?,
    actions?,
    index?: number
  ): void {
    const options = index ? {} : { index: index };
    const componentRef: ComponentRef<typeof component> =
      containerRef.createComponent(component, options);
    if (data) {
      for (const key in data) {
        componentRef.instance[key] = data[key];
      }
    }
    if (actions) {
      for (const key in actions) {
        componentRef.instance[key] = getComponentAction(
          actions[key],
          this.store
        );
      }
    }
  }

  deleteComponents(
    messageContainerRef: ViewContainerRef | undefined,
    numberOfItems: number
  ): void {
    if (messageContainerRef) {
      for (let i = 0; i < numberOfItems; i++) {
        // the last view in the container is removed.
        messageContainerRef.remove();
      }
    }
  }
}
